import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import type { RoundName } from "../../domain/round/types";
import { ApiError, api, type RoundStaffSummary, type TournamentSummary } from "../../lib/api";
import { roundApi, type ScheduleValidation } from "../../lib/roundApi";
import { initialRoundName, initialSeason, initialTournamentId, persistRoundSelection } from "../../lib/roundSelection";
import {
  buildSchedule,
  clockToMinutes,
  createGrid,
  distributeCarBlock,
  groupFinishMinutes,
  importSchedule,
  manualBuilderProblems,
  minutesToClock,
  type DistributionMode,
  type ManualBuilderGroup,
  type ManualCarBlock,
  type ManualPlacement,
} from "./manual-round-builder-model";
import "./manual-round-builder.css";

const ROUNDS: RoundName[] = ["R1", "R2", "R3", "R4"];
const DRAFT_PREFIX = "v2-manual-round-builder";
const BLOCK_MIME = "application/x-golf-manual-car-block";

function chooseTournament(rows: TournamentSummary[]) {
  if (!rows.length) return "";
  const today = new Date().toISOString().slice(0, 10);
  const started = rows.filter((row) => row.startDate && row.startDate <= today);
  return (started.at(-1) || rows[0]).id;
}

function uid(prefix: string) {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function segmentLabel(segment: ManualPlacement["segment"]) {
  if (segment === "full18" || segment === "extraFull18") return "18";
  if (segment === "holes1to9" || segment === "extra1to9") return "1–9";
  return "10–18";
}

function scorerStaff(rows: RoundStaffSummary[]) {
  const scorers = rows.filter((row) => ["scorer", "scoring"].includes(String(row.role || "").trim().toLowerCase()));
  return scorers.length ? scorers : rows;
}

function draftKey(tournamentId: string, round: RoundName) {
  return `${DRAFT_PREFIX}:${tournamentId}:${round}`;
}

type StoredDraft = {
  groups: ManualBuilderGroup[];
  placements: ManualPlacement[];
  blocks: ManualCarBlock[];
};

export function ManualRoundBuilderPage() {
  const queryClient = useQueryClient();
  const loadedKey = useRef("");
  const [season, setSeason] = useState(initialSeason);
  const [tournamentId, setTournamentId] = useState(initialTournamentId);
  const [round, setRound] = useState<RoundName>(initialRoundName);
  const [groups, setGroups] = useState<ManualBuilderGroup[]>([]);
  const [placements, setPlacements] = useState<ManualPlacement[]>([]);
  const [blocks, setBlocks] = useState<ManualCarBlock[]>([]);
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [blockName, setBlockName] = useState("");
  const [notice, setNotice] = useState("");
  const [serverValidation, setServerValidation] = useState<ScheduleValidation | null>(null);
  const [gridCount, setGridCount] = useState(24);
  const [tee1Count, setTee1Count] = useState(12);
  const [tee10Count, setTee10Count] = useState(12);
  const [tee1First, setTee1First] = useState("07:30");
  const [tee10First, setTee10First] = useState("07:30");
  const [intervalMinutes, setIntervalMinutes] = useState(10);
  const [defaultPlayers, setDefaultPlayers] = useState<2 | 3>(3);

  const tournaments = useQuery({ queryKey: ["tournaments", season], queryFn: () => api.tournaments(season) });
  useEffect(() => {
    const rows = tournaments.data?.tournaments || [];
    if (rows.length && (!tournamentId || !rows.some((row) => row.id === tournamentId))) setTournamentId(chooseTournament(rows));
  }, [tournaments.data, tournamentId]);
  useEffect(() => { if (tournamentId) persistRoundSelection(tournamentId, round, season); }, [tournamentId, round, season]);

  const workspace = useQuery({
    queryKey: ["round-workspace", tournamentId, round],
    queryFn: () => api.roundWorkspace(tournamentId, round),
    enabled: Boolean(tournamentId),
    staleTime: 5_000,
  });
  const staff = useMemo(() => scorerStaff(workspace.data?.staff || []), [workspace.data?.staff]);
  const personById = useMemo(() => new Map(staff.map((person) => [person.personId, person])), [staff]);
  const vehicleById = useMemo(() => new Map((workspace.data?.resources.vehicles || []).map((vehicle) => [vehicle.id, vehicle])), [workspace.data?.resources.vehicles]);

  useEffect(() => {
    if (!tournamentId) return;
    const key = draftKey(tournamentId, round);
    loadedKey.current = "";
    let restored: StoredDraft | null = null;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) restored = JSON.parse(raw) as StoredDraft;
    } catch {
      restored = null;
    }
    setGroups(Array.isArray(restored?.groups) ? restored!.groups : []);
    setPlacements(Array.isArray(restored?.placements) ? restored!.placements : []);
    setBlocks(Array.isArray(restored?.blocks) ? restored!.blocks : []);
    setSelectedPeople([]);
    setBlockName("");
    setNotice(restored?.groups?.length ? "Przywrócono lokalny szkic manualnego kreatora." : "");
    setServerValidation(null);
    loadedKey.current = key;
  }, [tournamentId, round]);

  useEffect(() => {
    if (!tournamentId) return;
    const key = draftKey(tournamentId, round);
    if (loadedKey.current !== key) return;
    const payload: StoredDraft = { groups, placements, blocks };
    window.localStorage.setItem(key, JSON.stringify(payload));
  }, [tournamentId, round, groups, placements, blocks]);

  const selectedTournament = tournaments.data?.tournaments.find((item) => item.id === tournamentId);
  const staffByBase = useMemo(() => {
    const map = new Map<string, RoundStaffSummary[]>();
    for (const person of staff) {
      const key = person.baseName || "Hotel";
      map.set(key, [...(map.get(key) || []), person]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, "pl"));
  }, [staff]);
  const placementsByGroup = useMemo(() => {
    const map = new Map<string, ManualPlacement[]>();
    for (const item of placements) map.set(item.groupId, [...(map.get(item.groupId) || []), item]);
    return map;
  }, [placements]);
  const placementsByPerson = useMemo(() => {
    const map = new Map<string, ManualPlacement[]>();
    for (const item of placements) map.set(item.personId, [...(map.get(item.personId) || []), item]);
    return map;
  }, [placements]);
  const assignedFullPeople = useMemo(() => new Set(placements.filter((item) => item.segment === "full18").map((item) => item.personId)), [placements]);
  const problems = useMemo(() => manualBuilderProblems(groups, placements, staff), [groups, placements, staff]);
  const candidateSchedule = useMemo(() => tournamentId ? buildSchedule(tournamentId, round, groups, placements, staff) : null, [tournamentId, round, groups, placements, staff]);
  const readOnly = Boolean(workspace.data?.selectedSource === "confirmed" && !workspace.data.availability.working);
  const contextSearch = `?tournament=${encodeURIComponent(tournamentId)}&round=${round}&season=${season}&tab=board`;

  function generateGrid() {
    if (groups.length && !window.confirm("Zastąpić obecną siatkę grup? Przydziały do grup zostaną wyczyszczone, bloki samochodów zostaną.")) return;
    const next = createGrid({
      groupCount: gridCount,
      tee1Count,
      tee10Count,
      tee1FirstMinutes: clockToMinutes(tee1First),
      tee10FirstMinutes: clockToMinutes(tee10First),
      intervalMinutes,
      playerCount: defaultPlayers,
    });
    setGroups(next);
    setPlacements([]);
    setServerValidation(null);
    setNotice(`Utworzono ${next.length} grup. Wszystkie godziny i tee możesz teraz edytować ręcznie.`);
  }

  function addGroup() {
    const last = [...groups].sort((a, b) => a.number - b.number).at(-1);
    const sameTee = groups.filter((group) => group.tee === (last?.tee || 1)).sort((a, b) => a.startMinutes - b.startMinutes).at(-1);
    setGroups((current) => [...current, {
      id: uid("manual-group"),
      number: Math.max(0, ...current.map((group) => group.number)) + 1,
      tee: last?.tee || 1,
      startMinutes: (sameTee?.startMinutes ?? clockToMinutes(tee1First)) + intervalMinutes,
      playerCount: defaultPlayers,
      playerNames: [],
      tv: false,
    }]);
    setServerValidation(null);
  }

  function patchGroup(groupId: string, patch: Partial<ManualBuilderGroup>) {
    setGroups((current) => current.map((group) => group.id === groupId ? { ...group, ...patch } : group));
    setServerValidation(null);
  }

  function deleteGroup(groupId: string) {
    setGroups((current) => current.filter((group) => group.id !== groupId));
    setPlacements((current) => current.filter((item) => item.groupId !== groupId));
    setServerValidation(null);
  }

  function createBlock() {
    const people = [...new Set(selectedPeople)].filter((personId) => personById.has(personId));
    if (!people.length) { setNotice("Najpierw zaznacz osoby, które jadą razem."); return; }
    const name = blockName.trim() || `Auto ${blocks.length + 1}`;
    setBlocks((current) => [
      ...current.map((block) => ({ ...block, personIds: block.personIds.filter((personId) => !people.includes(personId)) })).filter((block) => block.personIds.length),
      { id: uid("manual-car"), name, personIds: people },
    ]);
    setSelectedPeople([]);
    setBlockName("");
    setNotice(`${name}: utworzono blok ${people.length} osób.`);
  }

  function importTransportBlocks() {
    const departures = workspace.data?.transport?.departures || [];
    if (!departures.length) { setNotice("Ta runda nie ma jeszcze dojazdu, z którego można utworzyć bloki."); return; }
    const activeIds = new Set(staff.map((person) => person.personId));
    const next: ManualCarBlock[] = [];
    const already = new Set<string>();
    for (const trip of departures) {
      const personIds = [trip.driverPersonId, ...trip.passengerPersonIds].filter((personId) => activeIds.has(personId) && !already.has(personId));
      if (!personIds.length) continue;
      personIds.forEach((personId) => already.add(personId));
      const vehicle = vehicleById.get(trip.vehicleId);
      next.push({ id: uid("manual-car"), name: vehicle?.name || `Auto ${next.length + 1}`, personIds });
    }
    setBlocks(next);
    setNotice(`Utworzono ${next.length} bloków na podstawie obecnego dojazdu.`);
  }

  function distribute(block: ManualCarBlock, mode: DistributionMode, anchorGroupId?: string) {
    const result = distributeCarBlock(groups, placements, block.personIds, mode, anchorGroupId);
    if (result.error) { setNotice(result.error); return; }
    setPlacements(result.placements);
    setServerValidation(null);
    const teeLabel = mode === "both" ? "Tee 1 + Tee 10" : mode === "tee1" ? "Tee 1" : "Tee 10";
    setNotice(`${block.name}: rozłożono ${block.personIds.length} osób na ${teeLabel}. Algorytm minimalizuje rozjazd czasu zakończenia.`);
  }

  function clearBlockPlacements(block: ManualCarBlock) {
    const ids = new Set(block.personIds);
    setPlacements((current) => current.filter((item) => !(ids.has(item.personId) && item.segment === "full18")));
    setServerValidation(null);
  }

  function dropBlock(event: React.DragEvent, mode: DistributionMode, anchorGroupId?: string) {
    event.preventDefault();
    const blockId = event.dataTransfer.getData(BLOCK_MIME) || event.dataTransfer.getData("text/plain");
    const block = blocks.find((item) => item.id === blockId);
    if (block) distribute(block, mode, anchorGroupId);
  }

  function addPersonToGroup(groupId: string, personId: string) {
    if (!personId) return;
    const duplicate = placements.some((item) => item.groupId === groupId && item.personId === personId && item.segment === "full18");
    if (duplicate) return;
    setPlacements((current) => [...current, { id: uid("manual-assignment"), groupId, personId, segment: "full18" }]);
    setServerValidation(null);
  }

  function removePlacement(id: string) {
    setPlacements((current) => current.filter((item) => item.id !== id));
    setServerValidation(null);
  }

  function importWorkingSchedule() {
    if (!workspace.data?.schedule) { setNotice("Ta runda nie ma jeszcze Working Schedule do wczytania."); return; }
    if ((groups.length || placements.length) && !window.confirm("Wczytać Working Schedule i zastąpić aktualny lokalny szkic?")) return;
    const imported = importSchedule(workspace.data.schedule);
    setGroups(imported.groups);
    setPlacements(imported.placements);
    setServerValidation(null);
    setNotice(`Wczytano Working Schedule: ${imported.groups.length} grup.`);
  }

  function clearDraft() {
    if ((groups.length || placements.length || blocks.length) && !window.confirm("Wyczyścić cały lokalny manualny szkic tej rundy?")) return;
    setGroups([]); setPlacements([]); setBlocks([]); setSelectedPeople([]); setServerValidation(null); setNotice("Wyczyszczono manualny szkic.");
  }

  const validateMutation = useMutation({
    mutationFn: async () => {
      if (!candidateSchedule) throw new Error("Brak planu do sprawdzenia.");
      return roundApi.validateSchedule(tournamentId, round, candidateSchedule);
    },
    onSuccess: (result) => {
      setServerValidation(result);
      setNotice(result.valid ? `Walidacja OK. ${result.warnings.length} ostrzeżeń.` : `Walidacja: ${result.blockers.length} blockerów.`);
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (readOnly) throw new Error("Runda jest zatwierdzona i tylko do odczytu.");
      if (!candidateSchedule) throw new Error("Brak planu do zapisania.");
      if (problems.length) throw new Error(problems[0]);
      const validation = await roundApi.validateSchedule(tournamentId, round, candidateSchedule);
      setServerValidation(validation);
      if (!validation.valid) throw new Error(validation.blockers[0]?.message || "Plan ma błędy blokujące.");
      return api.saveRoundWorkspace(tournamentId, round, {
        expectedRevision: workspace.data?.workspaceRevision || 0,
        schedule: candidateSchedule,
        transport: workspace.data?.transport || null,
        transportStale: true,
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["round-workspace", tournamentId, round], result);
      setNotice(`Zapisano manualny plan jako Working Schedule r${result.workspaceRevision}. Transport oznaczono do przeliczenia.`);
    },
  });

  const error = saveMutation.error || validateMutation.error || workspace.error || tournaments.error;
  const errorText = error instanceof ApiError ? error.message : error instanceof Error ? error.message : "";
  const coveredGroups = new Set(placements.filter((item) => item.segment === "full18" || item.segment === "extraFull18").map((item) => item.groupId)).size;

  return (
    <section className="mrb-page">
      <article className="panel mrb-context">
        <div><span className="eyebrow">MANUALNY KREATOR RUNDY</span><h2>{selectedTournament?.name || "Wybierz turniej"}</h2><p>Budujesz rundę od zera: liczba grup, tee, godziny i obsada. Blok ludzi z jednego auta możesz rozłożyć jednym ruchem tak, żeby kończyli możliwie blisko siebie.</p></div>
        <div className="mrb-context-controls">
          <label><span>Sezon</span><select value={season} onChange={(event) => setSeason(Number(event.target.value))}>{[2026, 2025, 2024].map((value) => <option key={value}>{value}</option>)}</select></label>
          <label className="mrb-tournament"><span>Turniej</span><select value={tournamentId} onChange={(event) => setTournamentId(event.target.value)}>{(tournaments.data?.tournaments || []).map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
          <div className="mrb-rounds">{ROUNDS.map((value) => <button type="button" key={value} className={round === value ? "active" : ""} onClick={() => setRound(value)}>{value}</button>)}</div>
        </div>
      </article>

      {readOnly ? <div className="mrb-notice warning">Runda jest zatwierdzona. Manualny kreator możesz oglądać, ale zapis jest zablokowany.</div> : null}
      {errorText || notice ? <div className={`mrb-notice ${errorText ? "error" : ""}`}>{errorText || notice}</div> : null}

      <div className="mrb-kpis">
        <article className="panel"><span>Grupy</span><strong>{groups.length}</strong><small>{groups.filter((group) => group.tee === 1).length} Tee 1 · {groups.filter((group) => group.tee === 10).length} Tee 10</small></article>
        <article className="panel"><span>Pokryte pełnym scorerem</span><strong>{coveredGroups}/{groups.length}</strong><small>{Math.max(0, groups.length - coveredGroups)} wolnych grup</small></article>
        <article className="panel"><span>Scorerzy</span><strong>{staff.length}</strong><small>{assignedFullPeople.size} z pełnym 18</small></article>
        <article className="panel"><span>Bloki aut</span><strong>{blocks.length}</strong><small>{blocks.reduce((sum, block) => sum + block.personIds.length, 0)} osób w blokach</small></article>
      </div>

      <article className="panel mrb-grid-maker">
        <header><div><span className="eyebrow">1 · SIATKA GRUP</span><h3>Utwórz tyle grup, ile faktycznie potrzebujesz</h3></div><div className="mrb-top-actions"><button type="button" className="secondary-button" onClick={importWorkingSchedule} disabled={!workspace.data?.schedule}>Wczytaj Working Schedule</button><button type="button" className="text-button danger" onClick={clearDraft}>Wyczyść szkic</button></div></header>
        <div className="mrb-grid-controls">
          <label><span>Liczba grup</span><input type="number" min={1} max={120} value={gridCount} onChange={(event) => setGridCount(Math.max(1, Number(event.target.value) || 1))} /></label>
          <label><span>Tee 1 · grup</span><input type="number" min={0} max={120} value={tee1Count} onChange={(event) => setTee1Count(Math.max(0, Number(event.target.value) || 0))} /></label>
          <label><span>Tee 10 · grup</span><input type="number" min={0} max={120} value={tee10Count} onChange={(event) => setTee10Count(Math.max(0, Number(event.target.value) || 0))} /></label>
          <label><span>Pierwszy Tee 1</span><input type="time" value={tee1First} onChange={(event) => setTee1First(event.target.value)} /></label>
          <label><span>Pierwszy Tee 10</span><input type="time" value={tee10First} onChange={(event) => setTee10First(event.target.value)} /></label>
          <label><span>Interwał</span><input type="number" min={1} max={60} value={intervalMinutes} onChange={(event) => setIntervalMinutes(Math.max(1, Number(event.target.value) || 10))} /></label>
          <label><span>Domyślnie zawodników</span><select value={defaultPlayers} onChange={(event) => setDefaultPlayers(Number(event.target.value) === 2 ? 2 : 3)}><option value={3}>3 · 5h</option><option value={2}>2 · 4h</option></select></label>
          <button type="button" className="primary-button" onClick={generateGrid}>Wygeneruj / zastąp siatkę</button>
        </div>
        <p className="mrb-help">To jest tylko szybki start. Po utworzeniu siatki możesz zmienić ręcznie numer, tee, godzinę i liczbę zawodników każdej pojedynczej grupy.</p>
      </article>

      <div className="mrb-main-layout">
        <aside className="mrb-left">
          <article className="panel mrb-people-card">
            <header><div><span className="eyebrow">2 · LUDZIE → BLOKI AUT</span><h3>Zaznacz kto jedzie razem</h3></div><strong>{selectedPeople.length}</strong></header>
            <div className="mrb-block-create"><input value={blockName} onChange={(event) => setBlockName(event.target.value)} placeholder={`Auto ${blocks.length + 1}`} /><button type="button" className="primary-button" onClick={createBlock} disabled={!selectedPeople.length}>Utwórz blok</button></div>
            <button type="button" className="secondary-button mrb-import-cars" onClick={importTransportBlocks} disabled={!workspace.data?.transport?.departures.length}>Utwórz bloki z obecnego dojazdu</button>
            <div className="mrb-staff-bases">{staffByBase.map(([baseName, people]) => <section key={baseName}><h4>{baseName}</h4>{people.map((person) => {
              const selected = selectedPeople.includes(person.personId);
              const inBlock = blocks.find((block) => block.personIds.includes(person.personId));
              return <label className={`mrb-person ${selected ? "selected" : ""}`} key={person.personId}><input type="checkbox" checked={selected} onChange={(event) => setSelectedPeople((current) => event.target.checked ? [...new Set([...current, person.personId])] : current.filter((id) => id !== person.personId))} /><span><strong>{person.displayName}</strong><small>{person.driverEligible ? "Kierowca" : "Scorer"}{inBlock ? ` · ${inBlock.name}` : placementsByPerson.get(person.personId)?.length ? " · ma przydział" : ""}</small></span></label>;
            })}</section>)}</div>
          </article>

          <article className="panel mrb-blocks-card">
            <header><div><span className="eyebrow">BLOKI</span><h3>Przeciągnij całe auto</h3></div><strong>{blocks.length}</strong></header>
            <p>Przeciągnij blok na strefę po prawej albo użyj przycisków. Ponowne rozłożenie bloku zastępuje jego wcześniejsze pełne przydziały.</p>
            <div className="mrb-block-list">{blocks.map((block) => {
              const blockFinishes = block.personIds.flatMap((personId) => (placementsByPerson.get(personId) || []).filter((item) => item.segment === "full18").map((item) => groups.find((group) => group.id === item.groupId)).filter(Boolean).map((group) => groupFinishMinutes(group!)));
              const spread = blockFinishes.length > 1 ? Math.max(...blockFinishes) - Math.min(...blockFinishes) : 0;
              return <section className="mrb-car-block" key={block.id} draggable onDragStart={(event) => { event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData(BLOCK_MIME, block.id); event.dataTransfer.setData("text/plain", block.id); }}>
                <div className="mrb-car-head"><input value={block.name} onChange={(event) => setBlocks((current) => current.map((item) => item.id === block.id ? { ...item, name: event.target.value } : item))} /><button type="button" title="Usuń blok" onClick={() => setBlocks((current) => current.filter((item) => item.id !== block.id))}>×</button></div>
                <div className="mrb-car-people">{block.personIds.map((personId) => <span key={personId}>{personById.get(personId)?.displayName || personId}{personById.get(personId)?.driverEligible ? <b>K</b> : null}</span>)}</div>
                <div className="mrb-car-meta"><span>{block.personIds.length} osób</span>{blockFinishes.length ? <strong>rozjazd końca: {spread} min</strong> : <small>jeszcze nie rozłożony</small>}</div>
                <div className="mrb-car-actions"><button type="button" onClick={() => distribute(block, "both")}>1 + 10</button><button type="button" onClick={() => distribute(block, "tee1")}>Tee 1</button><button type="button" onClick={() => distribute(block, "tee10")}>Tee 10</button><button type="button" className="clear" onClick={() => clearBlockPlacements(block)}>Wyczyść</button></div>
              </section>;
            })}{!blocks.length ? <div className="mrb-empty">Zaznacz nazwiska powyżej i utwórz pierwszy blok auta.</div> : null}</div>
          </article>
        </aside>

        <main className="mrb-board">
          <article className="panel mrb-drop-card">
            <header><div><span className="eyebrow">3 · AUTO-ROZŁOŻENIE BLOKU</span><h3>Upuść samochód w wybranej strefie</h3></div></header>
            <div className="mrb-drop-zones">
              <div className="both" onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropBlock(event, "both")}><strong>Tee 1 + Tee 10</strong><span>po równo ±1 osoba · minimalny rozjazd końca</span></div>
              <div onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropBlock(event, "tee1")}><strong>Tee 1</strong><span>kolejne wolne grupy · kończą razem</span></div>
              <div onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropBlock(event, "tee10")}><strong>Tee 10</strong><span>kolejne wolne grupy · kończą razem</span></div>
            </div>
          </article>

          <article className="panel mrb-groups-card">
            <header><div><span className="eyebrow">4 · PEŁNA RĘCZNA KONTROLA</span><h3>Grupy, tee, godziny i scorerzy</h3></div><button type="button" className="secondary-button" onClick={addGroup}>+ Dodaj grupę</button></header>
            <div className="mrb-table-wrap"><table className="mrb-table"><thead><tr><th>G</th><th>Tee</th><th>Start</th><th>Koniec</th><th>Zaw.</th><th>Zawodnicy / notatka</th><th>TV</th><th>Scorerzy</th><th></th></tr></thead><tbody>{[...groups].sort((a, b) => a.startMinutes - b.startMinutes || a.tee - b.tee || a.number - b.number).map((group) => {
              const groupPlacements = placementsByGroup.get(group.id) || [];
              const fullCount = groupPlacements.filter((item) => item.segment === "full18" || item.segment === "extraFull18").length;
              return <tr key={group.id} className={fullCount ? "covered" : "uncovered"} onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropBlock(event, group.tee === 1 ? "tee1" : "tee10", group.id)} title="Możesz upuścić tutaj cały blok auta — zostanie rozłożony kolejno od tej grupy na tym tee.">
                <td><input className="number" type="number" min={1} value={group.number} onChange={(event) => patchGroup(group.id, { number: Math.max(1, Number(event.target.value) || 1) })} /></td>
                <td><select value={group.tee} onChange={(event) => patchGroup(group.id, { tee: Number(event.target.value) === 10 ? 10 : 1 })}><option value={1}>1</option><option value={10}>10</option></select></td>
                <td><input type="time" value={minutesToClock(group.startMinutes)} onChange={(event) => patchGroup(group.id, { startMinutes: clockToMinutes(event.target.value, group.startMinutes) })} /></td>
                <td><strong>{minutesToClock(groupFinishMinutes(group))}</strong></td>
                <td><select value={group.playerCount} onChange={(event) => patchGroup(group.id, { playerCount: Number(event.target.value) === 2 ? 2 : 3 })}><option value={3}>3</option><option value={2}>2</option></select></td>
                <td><input value={group.playerNames.join(", ")} onChange={(event) => patchGroup(group.id, { playerNames: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) })} placeholder="opcjonalnie: nazwiska graczy" /></td>
                <td><input type="checkbox" checked={group.tv} onChange={(event) => patchGroup(group.id, { tv: event.target.checked })} /></td>
                <td className="mrb-assignees"><div>{groupPlacements.map((item) => <span key={item.id} className={item.segment === "full18" ? "full" : "split"}><b>{personById.get(item.personId)?.displayName || item.personId}</b><small>{segmentLabel(item.segment)}</small><button type="button" onClick={() => removePlacement(item.id)}>×</button></span>)}</div><select value="" onChange={(event) => addPersonToGroup(group.id, event.target.value)}><option value="">+ dodaj pełne 18…</option>{staff.map((person) => <option key={person.personId} value={person.personId}>{person.displayName}{person.driverEligible ? " [K]" : ""}</option>)}</select></td>
                <td><button type="button" className="mrb-delete-group" onClick={() => deleteGroup(group.id)}>Usuń</button></td>
              </tr>;
            })}</tbody></table>{!groups.length ? <div className="mrb-empty table">Najpierw wygeneruj siatkę albo dodaj grupy ręcznie.</div> : null}</div>
          </article>
        </main>
      </div>

      <article className={`panel mrb-save-card ${problems.length || serverValidation?.blockers.length ? "blocked" : ""}`}>
        <div><span className="eyebrow">5 · WORKING SCHEDULE</span><h3>{problems.length ? "Plan jeszcze niekompletny" : serverValidation?.valid ? "Plan sprawdzony i gotowy" : "Sprawdź i zapisz"}</h3><p>{problems[0] || (serverValidation?.warnings.length ? `${serverValidation.warnings.length} ostrzeżeń — nie blokują zapisu.` : "Zapis trafia do tego samego Working Schedule co solver. Istniejący transport zostaje zachowany, ale oznaczony do ponownego przeliczenia.")}</p>{serverValidation?.blockers.slice(0, 4).map((issue, index) => <small className="mrb-blocker" key={`${issue.code}-${index}`}>BLOCKER · {issue.message}</small>)}</div>
        <div className="mrb-save-actions"><button type="button" className="secondary-button" disabled={!candidateSchedule || validateMutation.isPending} onClick={() => validateMutation.mutate()}>{validateMutation.isPending ? "Sprawdzam…" : "Sprawdź plan"}</button><button type="button" className="primary-button" disabled={readOnly || !candidateSchedule || problems.length > 0 || saveMutation.isPending} onClick={() => saveMutation.mutate()}>{saveMutation.isPending ? "Zapisuję…" : "Zapisz jako Working Schedule"}</button>{workspace.data?.schedule ? <Link className="secondary-button" to={`/generator${contextSearch}`}>Otwórz pełną edycję</Link> : null}</div>
      </article>
    </section>
  );
}
